import { useState, useEffect } from 'react';
import { Modal } from '../common/Modal';
import { PhoneInput } from '../common/PhoneInput';
import { SmsConsentDisclosure } from '../common/SmsConsentDisclosure';
import { SMS_CONSENT_SCRIPT_VERBAL } from '../../constants';
import { isValidEmail } from '../../utils/phone';

interface InstantVisitModalProps {
  open: boolean;
  medium: string;
  error?: string | null;
  onClose: () => void;
  onConfirm: (data: { patientName: string; patientEmail: string; patientPhone: string; smsConsent: boolean }) => void;
}

const mediumTitles: Record<string, string> = {
  video: 'New Instant Video Visit',
  phone: 'New Instant Phone Visit',
  sms: 'New Instant SMS Text Visit',
};

export function InstantVisitModal({ open, medium, error, onClose, onConfirm }: InstantVisitModalProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [smsConsent, setSmsConsent] = useState(false);
  const [nameError, setNameError] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [phoneError, setPhoneError] = useState(false);

  useEffect(() => {
    if (open) {
      setName(''); setEmail(''); setPhone(''); setSmsConsent(false);
      setNameError(false); setEmailError(false); setPhoneError(false);
    }
  }, [open]);

  const handleConfirm = () => {
    setNameError(false); setEmailError(false); setPhoneError(false);
    if (!name.trim()) { setNameError(true); return; }
    if (medium === 'sms' && !phone.trim()) { setPhoneError(true); return; }
    if ((medium === 'video' || medium === 'phone') && !email.trim()) { setEmailError(true); return; }
    if (email.trim() && !isValidEmail(email.trim())) { setEmailError(true); return; }
    onConfirm({ patientName: name.trim(), patientEmail: email.trim(), patientPhone: phone.trim(), smsConsent });
  };

  const isSms = medium === 'sms';
  const showConsentGroup = !isSms && phone.trim().length > 0;

  return (
    <Modal open={open} onClose={onClose} maxWidth="450px" ariaLabel={mediumTitles[medium]}>
      <h3 className="mb-1">{mediumTitles[medium] || 'New Visit'}</h3>
      <div className="form-group">
        <label htmlFor="patientNameInput">Patient Name</label>
        <input
          type="text" id="patientNameInput" placeholder="Enter patient name" maxLength={200}
          value={name} onChange={(e) => setName(e.target.value)}
          style={nameError ? { borderColor: 'var(--error-color)' } : undefined}
          onKeyDown={(e) => { if (e.key === 'Enter') handleConfirm(); }}
        />
      </div>
      <div className="form-group">
        <label htmlFor="patientEmailInput">
          {isSms ? 'Patient Email ' : 'Patient Email'}
          {isSms ? <span className="text-muted text-sm">(optional)</span> : null}
        </label>
        <input
          type="email" id="patientEmailInput" placeholder="patient@example.com" maxLength={200}
          value={email} onChange={(e) => setEmail(e.target.value)}
          style={emailError ? { borderColor: 'var(--error-color)' } : undefined}
        />
      </div>
      <div className="form-group">
        <label htmlFor="patientPhoneInput">
          {isSms ? 'Patient Phone' : 'Patient Phone '}
          {!isSms ? <span className="text-muted text-sm">(optional)</span> : null}
        </label>
        <PhoneInput
          id="patientPhoneInput" value={phone} onChange={setPhone}
          style={phoneError ? { borderColor: 'var(--error-color)' } : undefined}
        />
      </div>
      {showConsentGroup && (
        <div className="form-group">
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontWeight: 'normal' }}>
            <input type="checkbox" checked={smsConsent} onChange={(e) => setSmsConsent(e.target.checked)} style={{ flexShrink: 0, width: '16px', height: '16px' }} />
            <span className="text-sm">Patient has consented to receive SMS reminders <SmsConsentDisclosure intro="Read this to the patient for their consent:" script={SMS_CONSENT_SCRIPT_VERBAL} /></span>
          </label>
        </div>
      )}
      {error && <p style={{ color: 'var(--error-color)', fontSize: '0.875rem', margin: '0 0 0.5rem' }}>{error}</p>}
      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
        <button className="btn btn--outline btn--sm" onClick={onClose}>Cancel</button>
        <button className="btn btn--primary btn--sm" onClick={handleConfirm}>Create Visit</button>
      </div>
    </Modal>
  );
}
